import { useEffect, useState, useMemo, useCallback } from 'react';
import { useSearchParams } from 'react-router-dom';
import { FaFilter, FaTimes, FaChevronDown, FaChevronUp } from 'react-icons/fa';
import ItemModal from '../components/ItemModal';
import LoadingSpinner from '../components/LoadingSpinner';
import SearchBar from '../components/navigation/SearchBar';
import '../styles/Shop.css';

const Shop = ({ onAddToCart }) => {
  const [searchParams] = useSearchParams();
  const [selectedItem, setSelectedItem] = useState(null);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [products, setProducts] = useState([]);
  const [filteredProducts, setFilteredProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [categories, setCategories] = useState([]);

  // Simplified filter states
  const [searchTerm, setSearchTerm] = useState(searchParams.get('q') || '');
  const [selectedCategory, setSelectedCategory] = useState(searchParams.get('category') || 'all');
  const [priceRange, setPriceRange] = useState({ min: 0, max: 1000 });
  const [sortBy, setSortBy] = useState('newest');
  const [showFilters, setShowFilters] = useState(false);
  const [expandedFilters, setExpandedFilters] = useState({
    categories: true,
    price: true,
  });

  // Optimized mock data - removed unnecessary fields for better performance
  const mockProducts = useMemo(
    () => [
      {
        id: 1,
        name: 'Classic White T-Shirt',
        category: 'Tops',
        price: 29.99,
        image_url: '/assets/business-woman-professional-suit-posing-outdoors.jpg',
        colors: ['White', 'Black', 'Gray'],
      },
      {
        id: 2,
        name: 'Denim Jeans',
        category: 'Bottoms',
        price: 79.99,
        image_url: '/assets/blonde-woman-straw-hat-standing-near-wall-with-barefoot.jpg',
        colors: ['Blue', 'Black'],
      },
      {
        id: 3,
        name: 'Summer Dress',
        category: 'Dresses',
        price: 89.99,
        image_url: '/assets/Easy crochet skirt patterns.jpeg',
        colors: ['Pink', 'Blue', 'White'],
      },
      {
        id: 4,
        name: 'Leather Jacket',
        category: 'Tops',
        price: 199.99,
        image_url: '/assets/business-woman-professional-suit-posing-outdoors.jpg',
        colors: ['Black', 'Brown'],
      },
      {
        id: 5,
        name: 'Casual Sneakers',
        category: 'Shoes',
        price: 89.99,
        image_url: '/assets/blonde-woman-straw-hat-standing-near-wall-with-barefoot.jpg',
        colors: ['White', 'Black', 'Gray'],
      },
      {
        id: 6,
        name: 'Designer Handbag',
        category: 'Accessories',
        price: 149.99,
        image_url: '/assets/Easy crochet skirt patterns.jpeg',
        colors: ['Black', 'Brown', 'Red'],
      },
    ],
    [],
  );

  // Simplified fetch function for better performance
  const fetchProducts = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);

      // Simulate API delay but much shorter
      await new Promise(resolve => setTimeout(resolve, 200));

      setProducts(mockProducts);
      setFilteredProducts(mockProducts);

      const uniqueCategories = [...new Set(mockProducts.map(product => product.category))];
      setCategories(uniqueCategories);
    } catch (err) {
      setError('Failed to load products');
    } finally {
      setLoading(false);
    }
  }, [mockProducts]);

  // Fetch products on component mount only
  useEffect(() => {
    fetchProducts();
  }, [fetchProducts]);

  // Simplified filtering logic - much more performant
  useEffect(() => {
    let result = [...products];

    // Filter by search term
    if (searchTerm) {
      result = result.filter(product =>
        product.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
        product.category.toLowerCase().includes(searchTerm.toLowerCase())
      );
    }

    // Filter by category
    if (selectedCategory !== 'all') {
      result = result.filter(product => product.category === selectedCategory);
    }

    // Filter by price range
    result = result.filter(
      product => product.price >= priceRange.min && product.price <= priceRange.max
    );

    // Apply sorting
    switch (sortBy) {
      case 'price-low':
        result.sort((a, b) => a.price - b.price);
        break;
      case 'price-high':
        result.sort((a, b) => b.price - a.price);
        break;
      case 'name-asc':
        result.sort((a, b) => a.name.localeCompare(b.name));
        break;
      default:
        // Keep original order for 'newest'
        break;
    }

    setFilteredProducts(result);
  }, [products, searchTerm, selectedCategory, priceRange, sortBy]);

  const toggleFilter = filter => {
    setExpandedFilters(prev => ({
      ...prev,
      [filter]: !prev[filter],
    }));
  };

  const resetFilters = () => {
    setSearchTerm('');
    setSelectedCategory('all');
    setPriceRange({ min: 0, max: 1000 });
    setSortBy('newest');
  };

  const openModal = item => {
    setSelectedItem(item);
    setIsModalOpen(true);
  };

  const handleCloseModal = () => {
    setIsModalOpen(false);
    setSelectedItem(null);
  };

  const handleSearch = (query, filters) => {
    setSearchTerm(query);
    if (filters?.category && filters.category !== 'all') {
      setSelectedCategory(filters.category);
    }
  };

  return (
    <div className='shop-page'>
      <div className='shop-container'>
        <div className='shop-header'>
          <h1>Our Collections</h1>
          <p className='subtitle'>Discover your perfect style</p>
        </div>

        {/* Search Bar */}
        <div className='shop-search'>
          <SearchBar
            onSearch={handleSearch}
            placeholder='Search products, brands, styles...'
            className='shop-search-bar'
            showFilters={false}
          />
        </div>

        {/* Filter Toggle Button */}
        <button className='filter-toggle-btn' onClick={() => setShowFilters(!showFilters)}>
          {showFilters ? <FaTimes /> : <FaFilter />}
          <span>{showFilters ? 'Hide Filters' : 'Show Filters'}</span>
        </button>

        {/* Simplified Filters Panel */}
        <div className={`shop-filters ${showFilters ? 'show' : ''}`}>
          <div className='filter-header'>
            <h2>Filters</h2>
            <button className='reset-filters-btn' onClick={resetFilters}>
              Reset All
            </button>
          </div>

          {/* Categories */}
          <div className='filter-section'>
            <div className='filter-title' onClick={() => toggleFilter('categories')}>
              <h3>Categories</h3>
              {expandedFilters.categories ? <FaChevronUp /> : <FaChevronDown />}
            </div>
            {expandedFilters.categories && (
              <div className='filter-content'>
                <div className='category-list'>
                  <label className={selectedCategory === 'all' ? 'active' : ''}>
                    <input
                      type='radio'
                      name='category'
                      value='all'
                      checked={selectedCategory === 'all'}
                      onChange={() => setSelectedCategory('all')}
                    />
                    <span>All Categories</span>
                  </label>
                  {categories.map(category => (
                    <label key={category} className={selectedCategory === category ? 'active' : ''}>
                      <input
                        type='radio'
                        name='category'
                        value={category}
                        checked={selectedCategory === category}
                        onChange={() => setSelectedCategory(category)}
                      />
                      <span>{category}</span>
                    </label>
                  ))}
                </div>
              </div>
            )}
          </div>

          {/* Price Range */}
          <div className='filter-section'>
            <div className='filter-title' onClick={() => toggleFilter('price')}>
              <h3>Price Range</h3>
              {expandedFilters.price ? <FaChevronUp /> : <FaChevronDown />}
            </div>
            {expandedFilters.price && (
              <div className='filter-content'>
                <div className='price-range-inputs'>
                  <div className='price-input'>
                    <label>Min:</label>
                    <div className='input-prefix'>
                      <span>$</span>
                      <input
                        type='number'
                        value={priceRange.min}
                        onChange={e =>
                          setPriceRange({ ...priceRange, min: parseInt(e.target.value) || 0 })
                        }
                        min='0'
                      />
                    </div>
                  </div>
                  <div className='price-input'>
                    <label>Max:</label>
                    <div className='input-prefix'>
                      <span>$</span>
                      <input
                        type='number'
                        value={priceRange.max}
                        onChange={e =>
                          setPriceRange({ ...priceRange, max: parseInt(e.target.value) || 0 })
                        }
                        min='0'
                      />
                    </div>
                  </div>
                </div>
              </div>
            )}
          </div>

          {/* Sort Options */}
          <div className='filter-section'>
            <div className='filter-title'>
              <h3>Sort By</h3>
            </div>
            <div className='filter-content'>
              <select
                className='filter-select'
                value={sortBy}
                onChange={e => setSortBy(e.target.value)}
              >
                <option value='newest'>Newest First</option>
                <option value='price-low'>Price: Low to High</option>
                <option value='price-high'>Price: High to Low</option>
                <option value='name-asc'>Name: A-Z</option>
              </select>
            </div>
          </div>
        </div>

        {/* Shop Content */}
        <div className='shop-content'>
          <div className='shop-results-header'>
            <p className='results-count'>
              Showing {filteredProducts.length}{' '}
              {filteredProducts.length === 1 ? 'product' : 'products'}
            </p>
          </div>

          {loading ? (
            <div className='loading-container'>
              <LoadingSpinner />
              <p>Loading products...</p>
            </div>
          ) : error ? (
            <div className='error-container'>
              <h3>Oops! Something went wrong</h3>
              <p>{error}</p>
              <button onClick={fetchProducts} className='retry-btn'>
                Try Again
              </button>
            </div>
          ) : filteredProducts.length === 0 ? (
            <div className='no-products'>
              <h3>No products found</h3>
              <p>Try adjusting your filters to find what you're looking for.</p>
              <button onClick={resetFilters} className='reset-btn'>
                Reset Filters
              </button>
            </div>
          ) : (
            <div className='products-grid'>
              {filteredProducts.map((product, index) => (
                <div
                  key={product.id}
                  className='product-card fade-in'
                  style={{ animationDelay: `${index * 0.05}s` }}
                  onClick={() => openModal(product)}
                >
                  <div className='product-image-container'>
                    <img
                      src={product.image_url || '/assets/placeholder.jpg'}
                      alt={product.name}
                      className='product-image'
                      loading='lazy'
                      onError={e => {
                        e.target.src = '/assets/placeholder.jpg';
                      }}
                    />
                    <div className='product-overlay'>
                      <span>View Details</span>
                    </div>
                    <span className='product-price'>${product.price?.toFixed(2) || '0.00'}</span>
                  </div>
                  <div className='product-info'>
                    <h3 className='product-name'>{product.name}</h3>
                    <p className='product-category'>{product.category}</p>
                    {product.colors && product.colors.length > 0 && (
                      <div className='product-colors'>
                        {product.colors.slice(0, 3).map(color => (
                          <div
                            key={`${product.id}-color-${color}`}
                            className='color-dot'
                            style={{
                              backgroundColor: color.toLowerCase(),
                            }}
                            title={color}
                          />
                        ))}
                        {product.colors.length > 3 && (
                          <span className='more-colors'>+{product.colors.length - 3}</span>
                        )}
                      </div>
                    )}
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>

      {/* Item Modal */}
      {isModalOpen && selectedItem && (
        <ItemModal item={selectedItem} onClose={handleCloseModal} onAddToCart={onAddToCart} />
      )}
    </div>
  );
};

export default Shop;
